import { useTranslation } from 'react-i18next'
import { addDays, format, parseISO } from 'date-fns'
import { it as itLocale, enUS } from 'date-fns/locale'
import { cn } from '../../lib/utils'

interface DaySelectorRowProps {
  weekStart: string
  selectedDate: string | null
  onSelect: (date: string | null) => void
  disabled?: boolean
}

export function DaySelectorRow({ weekStart, selectedDate, onSelect, disabled = false }: DaySelectorRowProps) {
  const { i18n } = useTranslation()
  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS
  const start = parseISO(weekStart)

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(start, i)
    return {
      iso: format(d, 'yyyy-MM-dd'),
      label: format(d, 'EEEEEE', { locale: dateFnsLocale }),
    }
  })

  function handleClick(iso: string) {
    if (disabled) return
    if (iso === selectedDate) {
      onSelect(null)
    } else {
      onSelect(iso)
    }
  }

  return (
    <div
      className={cn(
        'flex gap-1 mt-1.5 pl-[52px]',
        disabled && 'opacity-50 pointer-events-none'
      )}
    >
      {days.map((d) => {
        const active = d.iso === selectedDate
        return (
          <button
            key={d.iso}
            type="button"
            disabled={disabled}
            onClick={() => handleClick(d.iso)}
            aria-pressed={active}
            className={cn(
              'flex-1 h-7 rounded-md text-xs font-medium border transition-colors capitalize',
              active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-border hover:bg-accent'
            )}
          >
            {d.label}
          </button>
        )
      })}
    </div>
  )
}
